/* @flow */
/* eslint-env es6,browser */

//set global variable needed for A9 library
var _amznads = window.amznads || {};
window.amznads = _amznads;

import * as u from '../utils';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/a9
 */
/**
 * @global
 * @typedef {object} a9Config
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=AMZ_BID]
 * @property {string} id
 */
/**
 * @description Implements functionality for amazon (a9) header bidding
 * @extends _bidbase
 * @requires module:utils
 */
class _a9bid extends bidbase {
  _config: a9Config;
  _DefConfig: a9Config;
  _bFirst: boolean;
  constructor() {
    super('A9');
    this._bFirst = true;

    this._DefConfig = u.extend(true, {}, this._DefConfig, {
      id: '',
      qpKey: 'AMZ_BID'
    });
    this._config = u.extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['script', 'id'];
    this._renderType = 'func';
    this._renderFunc = _amznads.renderAd;
    this._initCheck = {
      context: window.amznads,
      func: 'getAdsCallback'
    };
  }

  /**
   * getter for qpKey
   * @param {string} pos - Position name
   * @returns {string} - QP Key
   */
  getQPKey(): string {
    return this._config.qpKey;
  }

  /**
   * getter for posKey
   * @param {string} pos - Position name
   * @returns {string} - Position key
   */
  getPosKey(pos: string): string {
    return this._config.posTokenMap[pos];
  }

  /**
   * getter for LineId
   * @param {string} pos - Position name
   * @returns {string} - Line ID
   */
  getLineId(pos: string): string {
    return this._config.posTokenMap[pos];
  }

  /**
   * Set amazon params and resolve/reject in amazon callback
   * @returns {initStatus} - Pre-init check status
   * @access private
   */
  _preInit(): initStatus {
    //configure AMZN params
    _amznads.asyncParams = {
      id: this._config.id,
      timeout: this._config.timeout
    };
    return u.F_INITED;
  }

  /**
   * Process bid response. Response Format:
   * AMZN tokens will have position1<sep>price_point,position2<sep>price_point.
   * @param {Array<string>} posList - array of positions
   * @param {string} tokens - Array of tokens
   * @param {Object.<string, hbPosData>} ret - Return data
   * @returns {Object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    tokens: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    var rx = /t|p/,
      tokenMap = {},
      a,
      res = {},
      prefix = '',
      slot = 0;

    //Build object map here to return necessary values
    //This ensures that unique & highest bidding(slot)
    u.logDebug(`${this._name} processing tokens ${tokens.join(',')}`);
    tokens.forEach(token => {
      a = token.split(rx);
      if (a.length !== 2) {
        return;
      }
      prefix = a[0];
      slot = parseInt(a[1], 10);

      //if we get multiple buckets for the same position, use the lowest one
      if (!u.isObject(tokenMap[prefix]) || slot < tokenMap[prefix].slot) {
        tokenMap[prefix] = {
          slot: slot,
          token: token
        };
      }
    });

    posList.forEach(pos => {
      prefix = this.getPosKey(pos);
      res = tokenMap[prefix];
      if (res) {
        this._handleBidPosData(ret, pos, res.token, res.token);
      }
    });

    return ret;
  }

  _getAdsCallback(
    posList: Array<string>,
    resolve: Function,
    ret: hbPartnerData
  ) {
    //Give sometime for A9 to execute the JSONP callback
    //only then data will be available with us
    setTimeout(() => {
      var tok = _amznads.getTokens();
      ret = this._processBidResponse(posList, tok, ret);
      resolve(ret);
    }, 0);
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - HB Position list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    posList.forEach(
      pos => (ret[pos]._QP[this.getQPKey()] = `${this.getPosKey(pos)}p0`)
    );
    return new Promise(resolve => {
      if (this._bFirst) {
        //We already got some bidding when we load the AMZN library first
        //Let's use it. No need to send request again
        this._bFirst = false;
        this._getAdsCallback(posList, resolve, ret);
        return;
      }
      //Not the first time, let's send request now
      _amznads.getAdsCallback(this._config.id, () => {
        this._getAdsCallback(posList, resolve, ret);
      });
    });
  }

  /**
   * - Override adjust bid price (cpm)
   * @param {string|number} slot - slot
   * @returns {string|number} - slot
   * @access private
   */
  _bidPriceAdjust(slot: string | number): string | number {
    return slot;
  }
}

//add bidder to bidmanager
let a9bid = new _a9bid();
bm.addBidder('a9', a9bid);
export default a9bid;
